import React, { type UIEvent, useRef, useEffect, useCallback } from "react";
import Link from "next/link";
import { twJoin } from "tailwind-merge";
import debounce from "lodash.debounce";
import { type Column, SupDataGrid } from "@repo/core-ui";

import type { TRow } from "./types";

interface TableProps {
  className?: string;
  loading?: boolean;
  updating?: boolean;
  rows?: TRow[];
  columns: Column<TRow>[];
  onScrollToBottom?: () => void;
}

const SCROLL_DELAY = 300; // ms
const BOTTOM_OFFSET = 10; // px

function isAtBottom({ currentTarget }: UIEvent): boolean {
  return currentTarget.scrollTop + BOTTOM_OFFSET >= currentTarget.scrollHeight - currentTarget.clientHeight;
}

export function Table({ className, loading = false, updating = false, rows = [], columns, onScrollToBottom = () => null }: TableProps) {
  const scrollToBottomHandlerRef = useRef(debounce(onScrollToBottom, SCROLL_DELAY));

  const handleScroll = useCallback(
    (event: Event) => {
      if (loading || updating || !isAtBottom(event as unknown as UIEvent)) return;

      scrollToBottomHandlerRef.current();
    },
    [loading, updating]
  );

  useEffect(() => {
    const container = document.querySelector(".ui-content");

    if (!container) return;

    container.addEventListener("scroll", handleScroll);

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  return (
    <SupDataGrid
      loading={loading}
      className={twJoin("scrollbar-table", className)}
      data={rows}
      columns={columns}
      rowId={row => `${row.hash}-${row.superformId}`}
      renderRow={(key, row, defaultRowEl) =>
        !Array.isArray(row) ? (
          <Link key={key} prefetch={false} href={`https://scan.superform.xyz/tx/${row.hash}?superformId=${row.superformId}`} target="_blank" className="contents">
            {defaultRowEl}
          </Link>
        ) : (
          defaultRowEl
        )
      }
    />
  );
}
